import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Building2, ChevronDown, Check, Plus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * ActiveBuildingBanner
 * ====================
 * Pinned to the top-left of every authenticated page.
 * Shows the name of the building the user is currently viewing,
 * with a quick switcher dropdown if they belong to multiple buildings.
 *
 * Hidden on:
 *  - Anonymous routes (no user)
 *  - The login / landing / public invite pages
 *  - The Platform Owner dashboard (which already has its own building UI)
 */

const HIDE_ON_PREFIXES = [
  '/login', '/signup', '/free-trial', '/agent-signup',
  '/forgot-password', '/reset-password', '/verify-email',
  '/confirm-email-change', '/invite/', '/find-building',
  '/building-onboarding', '/join/',
];

export default function ActiveBuildingBanner() {
  const { user, myBuildings, activeBuilding, switchBuilding } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);

  // Don't render on public/auth pages
  if (!user) return null;
  if (location.pathname === '/') return null;
  if (HIDE_ON_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  const handleSwitch = async (hoa_id) => {
    if (hoa_id === activeBuilding?.hoa_id) {
      setOpen(false);
      return;
    }
    setSwitching(hoa_id);
    const ok = await switchBuilding(hoa_id);
    setSwitching(null);
    setOpen(false);
    if (ok) navigate(0);  // refresh so route loaders pick up new context
  };

  // No building linked yet → show CTA
  if (!activeBuilding) {
    if (!myBuildings || myBuildings.length === 0) {
      return (
        <button
          onClick={() => navigate('/find-building')}
          className="fixed top-3 left-3 z-40 flex items-center gap-2 bg-white shadow-md border border-slate-200 rounded-full pl-3 pr-4 py-1.5 text-sm hover:bg-slate-50"
          data-testid="active-building-add-cta"
        >
          <Plus className="w-4 h-4 text-indigo-600" />
          <span className="text-slate-700">Add a building</span>
        </button>
      );
    }
    return null;
  }

  const otherBuildings = (myBuildings || []).filter((b) => b.hoa_id !== activeBuilding.hoa_id);

  return (
    <div className="fixed top-3 left-3 z-40" data-testid="active-building-banner">
      <div className="relative">
        <button
          onClick={() => otherBuildings.length > 0 && setOpen(!open)}
          className="flex items-center gap-2 bg-white/95 backdrop-blur shadow-md border border-slate-200 rounded-full pl-3 pr-3 py-1.5 hover:shadow-lg transition-shadow"
          data-testid="active-building-name"
          aria-haspopup={otherBuildings.length > 0 ? 'menu' : undefined}
        >
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-emerald-500 flex items-center justify-center shrink-0">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <div className="flex flex-col items-start leading-tight max-w-[180px] sm:max-w-[260px]">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">Viewing</span>
            <span className="text-sm font-semibold text-slate-800 truncate">
              {activeBuilding.name || activeBuilding.building_name || 'Building'}
            </span>
          </div>
          {otherBuildings.length > 0 && (
            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
          )}
        </button>

        {open && otherBuildings.length > 0 && (
          <div
            className="absolute top-12 left-0 w-72 max-w-[calc(100vw-1.5rem)] bg-white rounded-xl shadow-xl border border-slate-200 py-2 max-h-[70vh] overflow-y-auto"
            data-testid="active-building-dropdown"
          >
            <p className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-400">Switch building</p>
            {(myBuildings || []).map((b) => {
              const isActive = b.hoa_id === activeBuilding.hoa_id;
              return (
                <button
                  key={b.hoa_id}
                  onClick={() => handleSwitch(b.hoa_id)}
                  disabled={switching === b.hoa_id}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 ${
                    isActive ? 'bg-emerald-50' : ''
                  }`}
                  data-testid={`switch-to-${b.hoa_id}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{b.name || b.building_name}</p>
                    {b.role && <p className="text-xs text-slate-500 capitalize">{b.role.replace(/_/g, ' ')}</p>}
                  </div>
                  {isActive && <Check className="w-4 h-4 text-emerald-600" />}
                </button>
              );
            })}
            <button
              onClick={() => { setOpen(false); navigate('/find-building'); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-indigo-600 hover:bg-indigo-50 border-t border-slate-100 mt-1"
              data-testid="active-building-add-another"
            >
              <Plus className="w-4 h-4" /> <span className="text-sm font-medium">Add another building</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
